import React, { useEffect } from 'react';
import { FiMic, FiX } from 'react-icons/fi';
import useVoiceSearch from '../hooks/useVoiceSearch';
import './VoiceSearchModal.css';

const STATUS_COPY = {
  idle: 'Tap the microphone to search by voice',
  requesting_permission: 'Requesting microphone access…',
  listening: 'Listening… speak now',
  processing: 'Processing your speech…',
  error: 'Something went wrong',
};

const VoiceSearchModal = ({
  open,
  onClose,
  onResult,
  lang = 'en-IN',
}) => {
  const {
    supported,
    unsupportedReason,
    status,
    errorMessage,
    interimTranscript,
    beginSession,
    endSession,
    retry,
  } = useVoiceSearch({
    lang,
    onResult: (text) => {
      onResult?.(text);
      onClose?.();
    },
  });

  useEffect(() => {
    if (!open) {
      endSession();
      return undefined;
    }
    if (!supported) return undefined;

    const timer = window.setTimeout(() => {
      beginSession();
    }, 50);

    return () => {
      window.clearTimeout(timer);
      endSession();
    };
  }, [open, supported]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const isListening = status === 'listening' || status === 'requesting_permission';
  const displayText = interimTranscript || (isListening ? '…' : '');
  const statusLabel = errorMessage || STATUS_COPY[status] || STATUS_COPY.idle;

  return (
    <div
      className="voice-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="voice-modal-title"
      onClick={onClose}
    >
      <div className="voice-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="voice-modal__close" onClick={onClose} aria-label="Close">
          <FiX size={20} />
        </button>

        <h2 id="voice-modal-title" className="voice-modal__title">Voice Search</h2>
        <p className="voice-modal__subtitle">
          {supported
            ? 'Say a city, location, or property type to search'
            : unsupportedReason}
        </p>

        {supported ? (
          <>
            <div className={`voice-modal__mic-wrap${isListening ? ' voice-modal__mic-wrap--active' : ''}`}>
              <span className="voice-modal__ring voice-modal__ring--1" aria-hidden />
              <span className="voice-modal__ring voice-modal__ring--2" aria-hidden />
              <span className="voice-modal__ring voice-modal__ring--3" aria-hidden />
              <div className="voice-modal__mic-btn" aria-hidden>
                <FiMic size={28} />
              </div>
            </div>

            {isListening && (
              <div className="voice-modal__bars" aria-hidden>
                {[0, 1, 2, 3, 4].map((i) => (
                  <span key={i} className="voice-modal__bar" style={{ animationDelay: `${i * 0.12}s` }} />
                ))}
              </div>
            )}

            <p
              className={`voice-modal__status${status === 'error' ? ' voice-modal__status--error' : ''}`}
              role="status"
              aria-live="polite"
            >
              {statusLabel}
            </p>

            {displayText && status !== 'error' && (
              <div className="voice-modal__transcript" aria-live="polite">
                &ldquo;{displayText}&rdquo;
              </div>
            )}

            <div className="voice-modal__actions">
              {status === 'error' ? (
                <button type="button" className="voice-modal__btn voice-modal__btn--primary" onClick={retry}>
                  Try Again
                </button>
              ) : (
                <button type="button" className="voice-modal__btn voice-modal__btn--ghost" onClick={onClose}>
                  Cancel
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="voice-modal__actions">
            <button type="button" className="voice-modal__btn voice-modal__btn--primary" onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceSearchModal;
